
import {
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import DashboardLayout from "../../shared/layouts/DashboardLayout";
import ProtectedRoute from "./ProtectedRoute";
import { useUser } from "../providers/userContext";
import { NotificationProvider } from "../providers/notificationContext";
import { SchoolDataProvider } from "../../features/academics/pages/time-table/schoolDataContext";
import { PERMISSIONS, ROLES, USER_ROLES } from "../../shared/constants/Constants";
import DriveRequiredRoute from "./DriveRequiredRoute.jsx";
import { Suspense, useCallback, useRef } from "react";
import { lazyWithPreload, preloadMatchingRoutes, RoutePreloadContext } from "./routePreload";
import AppLoader from "../../shared/components/app-loader/AppLoader";
import RouteErrorBoundary from "../../shared/components/route-error-boundary/RouteErrorBoundary";
import { INSTITUTION_TYPES } from "../../shared/constants/Constants";
import PlatformAccessRoute from "./PlatformAccessRoute";
import { ADMIN_LEAVE_ROLES } from "../../features/leaves/model/adminLeave";

const LoginScreen = lazyWithPreload(() => import("../../features/authentication/pages/login-screen/LoginScreen.jsx"));
const TeacherDashboard = lazyWithPreload(() => import("../../features/teachers/pages/dashboard/TeacherDashboard"));
const SubjectData = lazyWithPreload(() => import("../../features/academics/pages/subjects/SubjectData"));
const AcademicOverviewPage = lazyWithPreload(() => import("../../features/academics/pages/academic-overview/AcademicOverviewPage"));
const AddTeacher = lazyWithPreload(() => import("../../features/teachers/pages/dashboard/operation/add-teacher/AddTeacher"));
const TeacherData = lazyWithPreload(() => import("../../features/teachers/pages/teacher-data/TeacherData"));
const StudentData = lazyWithPreload(() => import("../../features/students/pages/student-data/StudentData"));
const AddStudent = lazyWithPreload(() => import("../../features/students/pages/operation/add-student/AddStudent"));
const ClassData = lazyWithPreload(() => import("../../features/classes/pages/class-data/ClassData"));
const AddClass = lazyWithPreload(() => import("../../features/classes/pages/add-class/AddClass"));
const FeeDashboard = lazyWithPreload(() => import("../../features/fees/pages/dashboard/FeeDashboard"));
const PrincipalDashboard = lazyWithPreload(() => import("../../features/dashboards/pages/dashboard/PrincipalDashboard"));
const UnauthorizedPage = lazyWithPreload(() => import("../pages/UnauthorizedPage"));
const TeacherClasses = lazyWithPreload(() => import("../../features/classes/pages/teachers/teacher-classes/TeacherClasses"));
const TeacherStudents = lazyWithPreload(() => import("../../features/students/pages/teachers/teacher-students/TeacherStudents"));
const TeacherAttendancePage = lazyWithPreload(() => import("../../features/attendance/pages/teachers/teacher-attendance-page/TeacherAttendancePage"));
const TimetableScreen = lazyWithPreload(() => import("../../features/academics/pages/time-table/TimetableScreen"));
const InstituteTimetable = lazyWithPreload(() => import("../../features/academics/pages/time-table/InstituteTimetable"));
const FeePaymentDashboard = lazyWithPreload(() => import("../../features/fees/pages/fees-payment/FeePaymentDashboard"));
const PrincipalAttendancePortal = lazyWithPreload(() => import("../../features/attendance/pages/attendance/PrincipalAttendancePortal"));
const PrincipalStudentAttendance = lazyWithPreload(() => import("../../features/attendance/pages/attendance/student-attendance/PrincipalStudentAttendance"));
const TeacherTimeTable = lazyWithPreload(() => import("../../features/academics/pages/teachers/timetable/TeacherTimeTable"));
const StudentDashboard = lazyWithPreload(() => import("../../features/students/pages/dashboard/StudentDashboard"));
const StudentProgressPage = lazyWithPreload(() => import("../../features/students/pages/progress/StudentProgressPage"));
const StaffStudentProgressPage = lazyWithPreload(() => import("../../features/students/pages/progress/StaffStudentProgressPage"));
const StudentAttendanceDashboard = lazyWithPreload(() => import("../../features/attendance/pages/students/student-attendance/StudentAttendanceDashboard"));
const StudentTimetable = lazyWithPreload(() => import("../../features/academics/pages/students/student-timetable/StudentTimetable"));
const StudentFeesDashboard = lazyWithPreload(() => import("../../features/fees/pages/students/student-fees/StudentFeesDashboard"));
const PasswordResetScreen = lazyWithPreload(() => import("../../features/authentication/pages/reset-password/PasswordResetScreen"));
const ChangePasswordScreen = lazyWithPreload(() => import("../../features/authentication/pages/reset-password/ChangePasswordScreen"));
const StudentBulkUpload = lazyWithPreload(() => import("../../features/students/pages/operation/add-student/StudentBulkUpload"));
const Profile = lazyWithPreload(() => import("../../features/users/pages/profile/Profile"));
const SchoolRBACManagement = lazyWithPreload(() => import("../../features/access-control/pages/SchoolRBACManagement"));
const GoogleDriveManager = lazyWithPreload(() => import("../../features/integrations/pages/integration/GoogleDriveManager"));
const DocumentDashboard = lazyWithPreload(() => import("../../features/documents/pages/documents/DocumentDashboard"));
const SchoolRegistrationForm = lazyWithPreload(() => import("../../features/schools/pages/school/SchoolRegistrationForm"));
const AdminHub = lazyWithPreload(() => import("../../features/settings/pages/setting/AdminHub"));
const AdminSetting = lazyWithPreload(() => import("../../features/settings/pages/setting/AdminSetting"));
const SupportPage = lazyWithPreload(() => import("../../features/support/pages/SupportPage"));
const PricingPage = lazyWithPreload(() => import("../../features/billing/pages/pricing/PricingPage"));
const SuperAdminBillingPage = lazyWithPreload(() => import("../../features/billing/pages/admin-billing/SuperAdminBillingPage"));
const TeacherProfile = lazyWithPreload(() => import("../../features/teachers/pages/teacher-profile/TeacherProfile"));
const ForgotPasswordScreen = lazyWithPreload(() => import("../../features/authentication/pages/forgot-password/ForgotPasswordScreen"));
const TimetableGenerator = lazyWithPreload(() => import("../../features/academics/pages/time-table/TimetableGenerator"));
const NotFoundPage = lazyWithPreload(() => import("../pages/NotFoundPage"));
const MonitoringDashboard = lazyWithPreload(() => import("../../features/system-administration/pages/monitoring-dashboard/MonitoringDashboard"));
const EmailManagement = lazyWithPreload(() => import("../../features/system-administration/pages/monitoring-dashboard/EmailManagement"));
const SuperAdminDashboard = lazyWithPreload(() => import("../../features/system-administration/pages/dashboard/SuperAdminDashboard"));
const SchoolsData = lazyWithPreload(() => import("../../features/system-administration/pages/schools/SchoolsData"));
const SchoolCalendar = lazyWithPreload(() => import("../../features/academics/pages/school-calendar/SchoolCalendar"));
const CreateEventPage = lazyWithPreload(() => import("../../features/events/pages/CreateEventPage"));
const SubjectManagement = lazyWithPreload(() => import("../../features/academics/pages/subjects/SubjectManagement"));
const ClassSubjectData = lazyWithPreload(() => import("../../features/academics/pages/subjects/ClassSubjectData"));
const ElectiveGroupsManagement = lazyWithPreload(() => import("../../features/academics/pages/subjects/ElectiveGroupsManagement"));
const StudentElectiveSelection = lazyWithPreload(() => import("../../features/academics/pages/subjects/StudentElectiveSelection"));
const TeacherSubjectsManagement = lazyWithPreload(() => import("../../features/teachers/pages/teacher-subject/TeacherSubjectsManagement"));
const SuperAdminRBAC = lazyWithPreload(() => import("../../features/access-control/pages/SuperAdminRBAC.jsx"));
const LeaveHistoryPage = lazyWithPreload(() => import("../../features/leaves/components/LeaveHistoryPage.jsx"));
const AdminLeaveManagementPage = lazyWithPreload(() => import("../../features/leaves/pages/AdminLeaveManagementPage.jsx"));
const AttendanceSchedulerDashboard = lazyWithPreload(() => import("../../features/system-administration/pages/scheduler/AttendanceSchedulerDashboard.jsx"));
const TeacherBulkUpload = lazyWithPreload(() => import("../../features/teachers/pages/dashboard/operation/add-teacher/TeacherBulkUpload.jsx"));
const ClassBulkUpload = lazyWithPreload(() => import("../../features/classes/pages/class-data/ClassBulkUpload.jsx"));
const PromotionIndex = lazyWithPreload(() => import("../../features/promotions/pages/PromotionIndex.jsx"));
const ActivityLogPage = lazyWithPreload(() => import("../../features/audit-log/pages/activity-log/ActivityLogPage.jsx"));
const FeeManagement = lazyWithPreload(() => import("../../features/fees/pages/feemanagement/Feemanagement.jsx"));
const SuperAdminPricingPage = lazyWithPreload(() => import("../../features/system-administration/pages/pricing-page/SuperAdminPricingPage.jsx"));
const PlatformManagementHome = lazyWithPreload(() => import("../../features/platform-management/pages/PlatformManagementHome.jsx"));
const InstitutionsPage = lazyWithPreload(() => import("../../features/platform-management/pages/InstitutionsPage.jsx"));
const LeadsPage = lazyWithPreload(() => import("../../features/platform-management/pages/LeadsPage.jsx"));
const TeamAccessPage = lazyWithPreload(() => import("../../features/platform-management/pages/TeamAccessPage.jsx"));
const NotificationSettings = lazyWithPreload(() => import("../../features/messaging/pages/SchoolMessagingPage.jsx"));
const SuperAdminMessagingPage = lazyWithPreload(() => import("../../features/messaging/pages/SuperAdminMessagingPage.jsx"));
const AdminNotificationSettings = lazyWithPreload(() => import("../../features/settings/pages/setting/AdminNotificationSettings.jsx"));
const MessagingCredentialTestPage = lazyWithPreload(() => import("../../features/messaging/pages/MessagingCredentialTestPage.jsx"));
const SuperAdminRagDashboard = lazyWithPreload(() => import("../../features/system-administration/pages/rag-dashboard/SuperAdminRagDashboard.jsx"));
const ExamPage = lazyWithPreload(() => import("../../features/exams/pages/ExamPage.jsx"));
const ExamCategoryPage = lazyWithPreload(() => import("../../features/exams/pages/ExamCategoryPage.jsx"));
const AcademicSessionPage = lazyWithPreload(() => import("../../features/academic-session/pages/AcademicSessionPage.jsx"));
const TeacherMarkEntry = lazyWithPreload(() => import("../../features/exams/pages/Teachermarkentry.jsx"));
const FeeStatsDashboard = lazyWithPreload(() => import("../../features/fees/pages/fee-stats/FeeStatsDashboard.jsx"));
const StudentReportCard = lazyWithPreload(() => import("../../features/exams/pages/Studentreportcard.jsx"));
const AdminAssignments = lazyWithPreload(() => import("../../features/assignments/pages/AdminAssignments.jsx"));
const StudentAssignments = lazyWithPreload(() => import("../../features/assignments/pages/StudentAssignments.jsx"));
const TeacherAssignments = lazyWithPreload(() => import("../../features/assignments/pages/TeacherAssignments.jsx"));
const EnquiryData = lazyWithPreload(() => import("../../features/enquiries/pages/EnquiryData.jsx"));
const EnquiryForm = lazyWithPreload(() => import("../../features/enquiries/pages/EnquiryForm.jsx"));
const BatchData = lazyWithPreload(() => import("../../features/batches/pages/BatchData.jsx"));
const BatchForm = lazyWithPreload(() => import("../../features/batches/pages/BatchForm.jsx"));
const BatchWorkflowsPage = lazyWithPreload(() => import("../../features/batches/pages/BatchWorkflowsPage.jsx"));
const MonthlyReportsPage = lazyWithPreload(() => import("../../features/reports/pages/MonthlyReportsPage.jsx"));
const RoleGuidePage = lazyWithPreload(() => import("../../features/help/pages/RoleGuidePage.jsx"));
const TeacherCourseAssignments = lazyWithPreload(() => import("../../features/teachers/pages/course-assignments/TeacherCourseAssignments.jsx"));
const PayrollDashboard = lazyWithPreload(() => import("../../features/teachers/pages/payroll/Payrolldashboard.jsx"));
const OrgChart = lazyWithPreload(() => import("../../features/teachers/pages/org-chart/OrgChart.jsx"));
const DocumentExplorerPage = lazyWithPreload(() => import("../../features/documents/pages/documents/index.jsx"));
const StudentCourseEnrollments = lazyWithPreload(() => import("../../features/students/pages/student-data/StudentCourseEnrollments.jsx"));
const InstituteAttendancePage = lazyWithPreload(() => import("../../features/attendance/pages/attendance/InstituteAttendancePage.jsx"));


function Routers() {
  const { user } = useUser();

  const OpenProfile = () => {
    return user?.roles[0] === ROLES.TEACHER ? <TeacherProfile /> : <Profile />;
  };

  const routeChildrenRef = useRef(null);
  const preloadRoute = useCallback((path) => preloadMatchingRoutes(routeChildrenRef.current, path), []);

  const routeChildren = (
    <>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/reset-password" element={<PasswordResetScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/support" element={<SupportPage />} />

      {/* ========== TEACHER ROUTES ========== */}
      <Route element={<ProtectedRoute allowedRoles={["TEACHER"]} />}>
        <Route path="teacher" element={<DashboardLayout />}>
          <Route index element={<TeacherDashboard />} />
          <Route path="dashboard" element={<TeacherDashboard />} />
          <Route
            path="course-assignments"
            element={user?.institutionType === INSTITUTION_TYPES[1] ? <TeacherCourseAssignments /> : <Navigate to="/unauthorized" replace />}
          />

          <Route
            path="leaves"
            element={<LeaveHistoryPage userRole="TEACHER" />}
          />
          <Route path="assignment">
            <Route index element={<TeacherAssignments />} />
          </Route>
          <Route
            path="batch/workflows"
            element={user?.institutionType === INSTITUTION_TYPES[1] ? <BatchWorkflowsPage /> : <Navigate to="/unauthorized" replace />}
          />

          {/* TEACHER: Exams Marks */}
          <Route path="exam" element={<TeacherMarkEntry />} />
          {/* TEACHER: My Classes - View assigned classes */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STAFF_ASSIGN_CLASSES]}
              />
            }
          >
            <Route path="classes" element={<TeacherClasses />} />
          </Route>

          {/* TEACHER: View Their Class Students (Different from full student management) */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STUDENT_VIEW_CLASS]}
              />
            }
          >
            <Route path="students">
              <Route index element={<TeacherStudents />} />
              <Route path=":studentId/progress" element={<StaffStudentProgressPage />} />
              <Route path="profile" element={<Profile />} />
              <Route path=":classId/:sectionId/profile" element={<Profile />} />
              <Route path=":classId/:sectionId" element={<TeacherStudents />} />

              {/* Mark attendance for their class students */}
              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.ACADEMIC_MARK_ATTENDANCE]}
                  />
                }
              >
                <Route path="attendance" element={user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteAttendancePage /> : <TeacherAttendancePage />} />
                <Route
                  path="attendance/:classId/:sectionId"
                  element={user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteAttendancePage /> : <TeacherAttendancePage />}
                />
              </Route>
            </Route>
          </Route>

          {/* TEACHER: Full Student Management (Admin-level access) */}
          {/* This is for teachers with STUDENT_VIEW_ALL permission */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STUDENT_VIEW_ALL]}
              />
            }
          >
            <Route path="student">
              <Route index element={<StudentData />} />
              <Route path=":studentId/progress" element={<StaffStudentProgressPage />} />
              <Route path="profile" element={<Profile />} />
              {user?.institutionType === INSTITUTION_TYPES[1] && <Route path="courses/:studentId" element={<StudentCourseEnrollments />} />}

              {/* Add student */}
              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.STUDENT_ADD]}
                  />
                }
              >
                <Route path="add" element={<AddStudent />} />
                <Route path="bulk-upload" element={<StudentBulkUpload />} />
              </Route>

              {/* Student attendance management */}
              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
                  />
                }
              >
                <Route
                  path="attendance"
                  element={user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteAttendancePage /> : <PrincipalStudentAttendance />}
                />
              </Route>
            </Route>
          </Route>

          {/* TEACHER: Staff Management (If they have permission) */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STAFF_VIEW_ALL]}
              />
            }
          >
            <Route path="teacher">
              <Route index element={<TeacherData />} />
              <Route path="profile" element={<TeacherProfile />} />

              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.STAFF_ADD]}
                  />
                }
              >
                <Route path="add" element={<AddTeacher />} />
                <Route path="bulk-add" element={<TeacherBulkUpload />} />
              </Route>

              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
                  />
                }
              >
                <Route
                  path="attendance"
                  element={<PrincipalAttendancePortal />}
                />
              </Route>
            </Route>
          </Route>

          {/* TEACHER: Class Management */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STAFF_ASSIGN_CLASSES]}
              />
            }
          >
            <Route path="class">
              <Route index element={<ClassData />} />
              <Route path="add" element={<AddClass />} />
              <Route path="bulk-add" element={<ClassBulkUpload />} />
            </Route>
          </Route>

          {/* TEACHER: Subject Management */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_GRADES]}
              />
            }
          >
            <Route path="subjects">
              <Route index element={<SubjectManagement />} />
              <Route path="management" element={<SubjectManagement />} />
              <Route path="data" element={<SubjectData />} />
              <Route path="class-subjects" element={<ClassSubjectData />} />
              <Route
                path="elective-group-management"
                element={<ElectiveGroupsManagement />}
              />
              <Route
                path="elective/assign"
                element={<StudentElectiveSelection />}
              />
            </Route>
          </Route>

          {/* TEACHER: Fee Management */}
          <Route path="fees">
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.FEE_VIEW_ALL]}
                />
              }
            >
              <Route path="student-record" element={<FeePaymentDashboard />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.FEE_COLLECT]}
                />
              }
            >
              <Route index element={<FeeDashboard />} />
            </Route>
          </Route>

          {/* TEACHER: Timetable */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
              />
            }
          >
            <Route path="timetable">
              <Route
                index
                element={
                  user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteTimetable /> : <SchoolDataProvider>
                    <TimetableScreen />
                  </SchoolDataProvider>
                }
              />
              <Route
                path="generator"
                element={<TimetableGenerator />}
              />
            </Route>
          </Route>

          {/* TEACHER: System Settings */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.SYSTEM_CONFIG]}
              />
            }
          >
            <Route path="setup">
              <Route index element={<AdminHub />} />
              <Route path="admin-setting" element={<AdminSetting />} />
              <Route path="school" element={<SchoolRegistrationForm />} />
              <Route path="drive" element={<GoogleDriveManager />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route
                element={
                  <ProtectedRoute
                    requiredPermissions={[PERMISSIONS.SYSTEM_USER_MANAGEMENT]}
                  />
                }
              >
                <Route
                  path="user-management"
                  element={<SchoolRBACManagement />}
                />
              </Route>
            </Route>
          </Route>

          {/* TEACHER: Own attendance and schedule */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
              />
            }
          >
            <Route
              path="attendance"
              element={<StudentAttendanceDashboard role={USER_ROLES.TEACHER} />}
            />
          </Route>

          <Route path="schedule" element={<TeacherTimeTable />} />
          <Route path="schedule/:sectionId" element={<TeacherTimeTable />} />
          <Route path="profile" element={<TeacherProfile />} />

          {/* TEACHER: Events */}
          <Route path="event">
            <Route
              index
              element={<SchoolCalendar userRole={USER_ROLES.TEACHER} />}
            />
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.COMM_SEND_NOTICE]}
                />
              }
            >
              <Route path="create" element={<CreateEventPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* ========== PRINCIPAL/ADMIN ROUTES ========== */}
      <Route
        element={
          <ProtectedRoute allowedRoles={["PRINCIPAL", "SCHOOL_ADMIN", "ADMIN"]} />
        }
      >
        <Route path="admin" element={<DashboardLayout />}>
          <Route element={<ProtectedRoute allowedRoles={ADMIN_LEAVE_ROLES} />}>
            <Route path="leaves" element={<AdminLeaveManagementPage />} />
          </Route>
          <Route path="messaging" element={<NotificationSettings />} />
          <Route index element={<PrincipalDashboard />} />
          <Route path="dashboard" element={<PrincipalDashboard />} />
          <Route path="promotions" element={<PromotionIndex />} />
          <Route element={<DriveRequiredRoute />}>
            <Route path="document" element={<DocumentExplorerPage />} />
          </Route>
          <Route path="exam">
            <Route index element={<ExamPage />} />
            <Route path="category" element={<ExamCategoryPage />} />
            <Route path="marks" element={<TeacherMarkEntry />} />
          </Route>
          <Route path="assignment">
            <Route index element={<AdminAssignments />} />
          </Route>
          <Route path="reports/monthly" element={<MonthlyReportsPage />} />
          <Route path="enquiry">
            <Route index element={<EnquiryData />} />
            <Route path="add" element={<EnquiryForm />} />
          </Route>
          <Route path="batch">
            <Route index element={<BatchData />} />
            <Route path="add" element={<BatchForm />} />
            <Route path="edit/:batchId" element={<BatchForm />} />
            <Route
              path="workflows"
              element={user?.institutionType === INSTITUTION_TYPES[1] ? <BatchWorkflowsPage /> : <Navigate to="/unauthorized" replace />}
            />
            
          </Route>
          <Route
            path="course-assignments"
            element={user?.institutionType === INSTITUTION_TYPES[1] ? <TeacherCourseAssignments /> : <Navigate to="/unauthorized" replace />}
          />
          {/* PRINCIPAL: Academic overview */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_GRADES]}
              />
            }
          >
            <Route path="academics" element={<AcademicOverviewPage />} />
          </Route>

          {/* PRINCIPAL: Subject Management */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_GRADES]}
              />
            }
          >
            <Route path="subjects">
              <Route index element={<SubjectManagement />} />
              <Route path="management" element={<SubjectManagement />} />
              <Route path="data" element={<SubjectData />} />
              <Route path="class-subjects" element={<ClassSubjectData />} />
              <Route
                path="elective-group-management"
                element={<ElectiveGroupsManagement />}
              />
              <Route
                path="elective/assign"
                element={<StudentElectiveSelection />}
              />
            </Route>
          </Route>

          {/* PRINCIPAL: Teacher Management */}
          <Route path="teacher">
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.STAFF_VIEW_ALL]}
                />
              }
            >
              <Route index element={<TeacherData />} />
               <Route path="org-chart" element={<OrgChart />} />
              <Route path="profile" element={<TeacherProfile />} />
              <Route path="payroll" element={<PayrollDashboard />} />
            </Route>

            <Route
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.STAFF_ADD]} />
              }
            >
              <Route path="add" element={<AddTeacher />} />
              <Route path="bulk-add" element={<TeacherBulkUpload />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.STAFF_ASSIGN_CLASSES]}
                />
              }
            >
              <Route
                path="teacher-subjects"
                element={<TeacherSubjectsManagement />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
                />
              }
            >
              <Route
                path="attendance"
                element={<PrincipalAttendancePortal />}
              />
            </Route>
          </Route>

          {/* PRINCIPAL: Student Management */}
          <Route path="student">
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[
                    PERMISSIONS.STUDENT_VIEW_ALL,
                    PERMISSIONS.STUDENT_VIEW_CLASS,
                  ]}
                />
              }
            >
              <Route index element={<StudentData />} />
              <Route path=":studentId/progress" element={<StaffStudentProgressPage />} />
              <Route path="profile" element={<Profile />} />
              {user?.institutionType === INSTITUTION_TYPES[1] && <Route path="courses/:studentId" element={<StudentCourseEnrollments />} />}
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.STUDENT_ADD]}
                />
              }
            >
              <Route path="add" element={<AddStudent />} />
              <Route path="bulk-upload" element={<StudentBulkUpload />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
                />
              }
            >
              <Route
                path="attendance"
                element={user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteAttendancePage /> : <PrincipalStudentAttendance />}
              />
              <Route path="attendance/mark/:classId/:sectionId" element={<TeacherAttendancePage />} />
            </Route>
          </Route>

          {/* PRINCIPAL: Class Management */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.STAFF_ASSIGN_CLASSES]}
              />
            }
          >
            <Route path="class">
              <Route index element={<ClassData />} />
              <Route path="add" element={<AddClass />} />
              <Route path="bulk-add" element={<ClassBulkUpload />} />
            </Route>
          </Route>

          {/* PRINCIPAL: Fee Management */}
          <Route path="fees">
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.FEE_VIEW_ALL]}
                />
              }
            >
              <Route path="stats" element={<FeeStatsDashboard />} />
              <Route path="student-record" element={<FeePaymentDashboard />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.FEE_COLLECT]}
                />
              }
            >
              <Route index element={<FeeDashboard />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.SYSTEM_USER_MANAGEMENT]}
                />
              }
            >
              <Route path="management" element={<FeeManagement />} />
            </Route>
          </Route>

          {/* PRINCIPAL: Timetable */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE]}
              />
            }
          >
            <Route path="timetable">
              <Route
                index
                element={
                  user?.institutionType === INSTITUTION_TYPES[1] ? <InstituteTimetable /> : <SchoolDataProvider>
                    <TimetableScreen />
                  </SchoolDataProvider>
                }
              />
              <Route
                path="generator"
                element={<TimetableGenerator />}
              />
            </Route>
          </Route>

          {/* PRINCIPAL: System Settings */}
          <Route path="messaging-credential-test" element={<MessagingCredentialTestPage />} />
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.SYSTEM_CONFIG]}
              />
            }
          >
            <Route path="setup">
              <Route index element={<AdminHub />} />
              <Route path="admin-setting" element={<AdminSetting />} />
              <Route path="school" element={<SchoolRegistrationForm />} />
              <Route path="drive" element={<GoogleDriveManager />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="messaging" element={<NotificationSettings />} />
              <Route
                path="academic-sessions"
                element={<AcademicSessionPage />}
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.SYSTEM_USER_MANAGEMENT]}
              />
            }
          >
            <Route
              path="setup/user-management"
              element={<SchoolRBACManagement />}
            />
          </Route>

          {/* PRINCIPAL: Events */}
          <Route path="event">
            <Route
              index
              element={<SchoolCalendar userRole={USER_ROLES.PRINCIPAL} />}
            />
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.COMM_SEND_NOTICE]}
                />
              }
            >
              <Route path="create" element={<CreateEventPage />} />
            </Route>
          </Route>
          <Route path="logs">
            <Route
              element={
                <ProtectedRoute
                  requiredPermissions={[PERMISSIONS.ACTIVITY_LOG_VIEW]}
                />
              }
            >
              <Route index element={<ActivityLogPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* ========== STUDENT ROUTES ========== */}
      <Route element={<ProtectedRoute allowedRoles={["STUDENT"]} />}>
        <Route path="student" element={<DashboardLayout />}>
          <Route index element={<StudentDashboard />} />
          <Route path="dashboard" element={<StudentDashboard />} />
          <Route path="performance" element={<Navigate to="/student/progress" replace />} />
          <Route path="progress" element={<StudentProgressPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="attendance" element={<StudentAttendanceDashboard />} />
          <Route
            path="leaves"
            element={<LeaveHistoryPage userRole="STUDENT" />}
          />
          <Route path="assignment">
            <Route index element={<StudentAssignments />} />
          </Route>
          <Route path="timetable" element={<StudentTimetable />} />
          <Route path="fee" element={<StudentFeesDashboard />} />
          <Route path="results" element={<StudentReportCard />} />

          <Route path="event">
            <Route
              index
              element={<SchoolCalendar userRole={USER_ROLES.STUDENT} />}
            />
          </Route>
        </Route>
      </Route>

      {/* ========== SUPER ADMIN ROUTES ========== */}
      <Route element={<ProtectedRoute allowedRoles={["SUPER_ADMIN"]} />}>
        <Route path="super_admin" element={<DashboardLayout />}>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route path="monitor" element={<MonitoringDashboard />} />
          <Route path="ask" element={<SuperAdminRagDashboard />} />
          <Route path="email-management" element={<EmailManagement />} />
          <Route path="school-onboard" element={<SchoolRegistrationForm />} />
          <Route path="school" element={<SchoolsData />} />
          <Route path="role-management" element={<SuperAdminRBAC />} />
          <Route path="pricing" element={<SuperAdminPricingPage />} />
          <Route path="notifications" element={<AdminNotificationSettings />} />
          <Route path="messaging" element={<SuperAdminMessagingPage />} />
          <Route path="dev/notifications" element={<Navigate to="/super_admin/messaging" replace />} />
          <Route path="messaging-credential-test" element={<MessagingCredentialTestPage />} />
          
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path="how-it-works" element={<DashboardLayout />}>
          <Route index element={<RoleGuidePage />} />
        </Route>
        <Route path="billing" element={<DashboardLayout />}>
          <Route index element={<SuperAdminBillingPage />} />
        </Route>
      </Route>

      {/* ========== PLATFORM MANAGEMENT ========== */}
      <Route element={<ProtectedRoute />}>
        <Route path="platform" element={<DashboardLayout />}>
          <Route element={<PlatformAccessRoute permissions={[
            "PLATFORM_SCHOOLS_VIEW", "PLATFORM_SCHOOLS_MANAGE",
            "PLATFORM_LEADS_VIEW", "PLATFORM_LEADS_MANAGE",
            "BILLING_VIEW", "BILLING_MANAGE", "BILLING_REFUND",
            "PLATFORM_PRICING_MANAGE",
          ]} />}>
            <Route index element={<PlatformManagementHome />} />
          </Route>
          <Route element={<PlatformAccessRoute permissions={["PLATFORM_SCHOOLS_VIEW", "PLATFORM_SCHOOLS_MANAGE"]} />}>
            <Route path="institutions" element={<InstitutionsPage />} />
          </Route>
          <Route element={<PlatformAccessRoute permissions={["PLATFORM_LEADS_VIEW", "PLATFORM_LEADS_MANAGE"]} />}>
            <Route path="leads" element={<LeadsPage />} />
          </Route>
          <Route element={<PlatformAccessRoute permissions={["PLATFORM_PRICING_MANAGE"]} globalOnly />}>
            <Route path="pricing" element={<SuperAdminPricingPage />} />
          </Route>
          <Route element={<ProtectedRoute allowedRoles={["SUPER_ADMIN"]} />}>
            <Route path="team-access" element={<TeamAccessPage />} />
          </Route>
        </Route>
      </Route>

      {/* ========== COMMON PROTECTED ROUTES ========== */}
      <Route
        element={
          <ProtectedRoute
            allowedRoles={["TEACHER", "STUDENT", "PRINCIPAL", "SCHOOL_ADMIN", "ADMIN", "SUPER_ADMIN"]}
          />
        }
      >
        <Route path="setting" element={<DashboardLayout />}>
          <Route index element={<ChangePasswordScreen />} />
          <Route path="profile" element={<OpenProfile />} />
          <Route path="change-password" element={<ChangePasswordScreen />} />
          <Route
            path="attendance/scheduler"
            element={
              <AttendanceSchedulerDashboard />
            }
          />
        </Route>

        <Route element={<DriveRequiredRoute />}>
          <Route path="document" element={<DashboardLayout />}>
            <Route index element={<DocumentDashboard />} />
            <Route path="list" element={<DocumentDashboard />} />
          </Route>
        </Route>
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </>
  );
  routeChildrenRef.current = routeChildren;

  return (
    <NotificationProvider>
    <RoutePreloadContext.Provider value={preloadRoute}>
    <RouteErrorBoundary>
    {/* Dashboard screens get their skeleton from DashboardLayout; this covers login and public pages. */}
    <Suspense fallback={<AppLoader />}>
    <Routes>{routeChildren}</Routes>
    </Suspense>
    </RouteErrorBoundary>
    </RoutePreloadContext.Provider>
    </NotificationProvider>
  );
}

export default Routers;
